import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { BrandMark } from '@/components/BrandLogo';

export default function RequireAuth({ children }) {
  const { isAuthenticated, loading, bootstrapError, membership, accessResolved, accessError, signOut } = useAuth();
  const location = useLocation();
  const [showSlow, setShowSlow] = useState(false);
  const resolving = loading || (isAuthenticated && !accessResolved);

  useEffect(() => {
    if (!resolving) {
      setShowSlow(false);
      return;
    }
    const t = setTimeout(() => setShowSlow(true), 3000);
    return () => clearTimeout(t);
  }, [resolving]);

  if (resolving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm text-center">
          <BrandMark tone="color" size={44} className="mx-auto" />
          <div className="mx-auto mt-4 h-5 w-5 rounded-full border-2 border-bronze border-t-transparent animate-spin" />
          <div className="mt-3 text-sm text-charcoal">Loading Alvora OS…</div>
          {bootstrapError && (
            <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-900 text-left">
              {bootstrapError}
            </div>
          )}
          {showSlow && (
            <div className="mt-6 space-y-3">
              <div className="text-xs text-muted-foreground">
                This is taking longer than usual. You can retry or sign out and
                try again.
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  data-testid="auth-loading-reload"
                  onClick={() => window.location.reload()}
                  className="text-xs rounded-md border border-border px-3 py-1.5 hover:bg-muted transition-colors duration-150"
                >
                  Reload
                </button>
                <button
                  type="button"
                  data-testid="auth-loading-signout"
                  onClick={async () => {
                    try { await supabase?.auth.signOut(); } catch { /* ignore */ }
                    window.location.href = '/login';
                  }}
                  className="text-xs rounded-md bg-bronze text-white px-3 py-1.5 hover:bg-bronze-hover transition-colors duration-150"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Fail closed: access could not be verified → neither the internal shell nor the portal is rendered
  if (accessError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm text-center space-y-3" data-testid="access-error">
          <BrandMark tone="color" size={44} className="mx-auto" />
          <h1 className="font-display text-xl text-charcoal">Access could not be verified</h1>
          <p className="text-sm text-muted-foreground">{accessError}</p>
          <div className="flex items-center justify-center gap-2">
            <button type="button" data-testid="access-error-retry" onClick={() => window.location.reload()} className="text-xs rounded-md bg-bronze text-white px-3 py-1.5 hover:bg-bronze-hover transition-colors duration-150">Retry</button>
            <button type="button" data-testid="access-error-signout" onClick={signOut} className="text-xs rounded-md border border-border px-3 py-1.5 hover:bg-muted transition-colors duration-150">Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  // No internal organisation membership → never render the internal shell; the portal independently checks project access
  if (!membership && !location.pathname.startsWith('/portal')) return <Navigate to="/portal" replace />;

  return children;
}
